import type { Cadence } from './cadence';

// ============================================================
// Retailer Catalog — data model (Product module, retailer-only)
// ============================================================
// Design + rationale: docs/features/retailer-catalog.md
//
// Store split (by ACCESS PATTERN, never by entity):
//   DynamoDB     — structured, queried, rendered, edited entities (this file's types)
//   S3 classic   — sitemap snapshots (diff source) + product-index.json (matching file)
//   S3 Vectors   — one embedding per item (search); metadata is NOT modeled here
//
// Object hierarchy: Tenant → Brand → Catalogue(s) → Products + Categories.
// One catalogue = one market = one language (language/geoZone/shippingZone are
// catalogue properties, resolved app-side from catalogueId — not per-item).

/** `<brandId>#<slug>` — globally unique, sorts by brand. e.g. `b_abc123#auchan-fr`. */
export type CatalogueId = string;

/** How a catalogue was ingested. `upload` is the WAF fallback (Decathlon/ManoMano). */
export type CatalogueSourceType = 'sitemap' | 'feed' | 'upload';

export type CatalogueStatus = 'draft' | 'active';

/** A catalogue item is a product or a (sub)category — both are optimizable,
 *  linkable, and embedded into the shared vector index. */
export type CatalogueItemType = 'product' | 'category';

/** Signals a measurement run uses to detect that the retailer/its products were
 *  recommended in an AI answer. Configured per catalogue (see Catalogue.matchSignals). */
export type CatalogueMatchSignal = 'domain' | 'name' | 'ref' | 'brand';

/** Default: domain + name (the marketplace case — Auchan). A manufacturer brand
 *  would drop `domain`; a distinctive-SKU retailer would add `ref`. */
export const DEFAULT_MATCH_SIGNALS: CatalogueMatchSignal[] = ['domain', 'name'];

/** Allowed catalogue icon keys (MUI icon names, snake_case → component map on the
 *  frontend). A closed set so the server can validate; purely cosmetic, lets users
 *  tell catalogues apart at a glance in lists + selectors. */
export const CATALOGUE_ICONS = [
  'store',
  'shopping_cart',
  'shopping_bag',
  'category',
  'inventory',
  'local_offer',
  'checkroom',
  'chair',
  'kitchen',
  'devices',
  'sports_soccer',
  'restaurant',
  'local_grocery_store',
  'pets',
  'spa',
  'toys',
  'build',
  'directions_car',
  'menu_book',
  'watch',
  'diamond',
  'local_florist',
  'fitness_center',
  'liquor',
] as const;
export type CatalogueIcon = (typeof CATALOGUE_ICONS)[number];
export const DEFAULT_CATALOGUE_ICON: CatalogueIcon = 'store';

/** Per-item fetch state for the Phase-2 optimization pass. `blocked` = WAF page
 *  recovered via Serper-scrape (partial), `pending` = not yet fetched (retryable),
 *  `gone` = the URL is permanently removed from the site (HTTP 404/410) — TERMINAL,
 *  a re-run won't help (the retailer's own sitemap may still list it). */
export type FetchStatus = 'fetched' | 'blocked' | 'pending' | 'gone';

// ------------------------------------------------------------
// Scope rules — reduce a large sitemap (Auchan 331k) to ≤50k activated items.
// Reuses the brand.auditConfig glob matcher (** any incl /, * excl /, ? one char).
// ------------------------------------------------------------

export interface CatalogueScopeRules {
  /** Glob/regex patterns to KEEP (empty = keep all not excluded). */
  include: string[];
  /** Glob/regex patterns to DROP (wins over include). */
  exclude: string[];
}

/**
 * PERSIST ceiling: how many scoped items `createFromPreview` writes to DynamoDB.
 * Bounded by the synchronous BatchWrite budget under the 29s API-Gateway timeout
 * (chunks of 25 drained serially — well above this would time out mid-write).
 * Also the matching-dictionary size (Aho-Corasick stays fast + low false-positive).
 */
export const CATALOGUE_MAX_ITEMS = 50_000;

/**
 * HARVEST ceiling: how many candidates `preview` collects into the S3 scoping
 * cache (search + checkbox-select run against this). Higher than the persist cap
 * so a user can SEARCH the full catalogue of a large retailer while the SCOPED
 * subset they persist stays ≤ CATALOGUE_MAX_ITEMS. Memory-bound only (cached to
 * S3, never shipped to the browser); server-side search re-reads it, so raising
 * this trades a slower per-search read for fuller coverage.
 */
export const CATALOGUE_HARVEST_MAX_ITEMS = 150_000;

// ------------------------------------------------------------
// META item  — SK = META  (one per catalogue)
// ------------------------------------------------------------

export interface Catalogue {
  catalogueId: CatalogueId;
  brandId: string;
  tenantId: string;
  /** Display name, e.g. "Auchan France". */
  name: string;
  /** Optional free-text description of what this catalogue covers. Reused as
   *  context when the catalogue is selected in content creation + segmentation. */
  description?: string;
  /** Cosmetic icon key (see CATALOGUE_ICONS) to differentiate catalogues in the UI. */
  icon?: CatalogueIcon;
  /** Root domain used for the "is cited" URL match, e.g. `www.auchan.fr`. */
  domain: string;
  /** Catalogue-level properties (implicit for every item — NOT stored per-item). */
  language: string; // ISO 639-1
  geoZone?: string;
  shippingZone?: string;

  sourceType: CatalogueSourceType;
  /** Discovered sitemap leaf (urlset) URLs; empty for pure uploads. */
  sitemapUrls: string[];
  /** Sitemap fetch is WAF-challenged from a datacenter IP → needs Serper-scrape or upload. */
  wafBlocked: boolean;

  totalProducts: number;
  totalCategories: number;
  /** Distinct languages seen (usually just [language]; >1 only for bilingual markets). */
  languages: string[];

  scopeRules: CatalogueScopeRules;

  /** How a measurement run decides the retailer/its products were recommended.
   *  Retailer-shaped, NOT shopper-shaped, so it lives on the catalogue:
   *   - domain : a cited link resolves to this catalogue's domain (proves the
   *              RETAILER was recommended — the only reliable signal for a
   *              marketplace selling generic goods, e.g. Auchan + Coca-Cola).
   *   - name   : a product NAME appears in the answer (right for a manufacturer
   *              brand where being named IS the win, e.g. Coca-Cola's own site).
   *   - ref    : a product REFERENCE/SKU token appears (distinctive own-brand
   *              refs, e.g. Decathlon).
   *   - brand  : a configured brand term appears (matchBrandTerms).
   *  `domain` present ⇒ retailer-mode (name/ref-only match = "assortment leak").
   *  `domain` absent  ⇒ brand-mode (a name/ref/brand match counts as covered). */
  matchSignals?: CatalogueMatchSignal[];
  /** Brand terms for the `brand` signal (lowercased match), e.g. ["decathlon","quechua"]. */
  matchBrandTerms?: string[];
  /** User-defined wording sections generated in addition to the 6 fixed ones. */
  customSections?: CatalogueCustomSection[];
  /** Fixed sections the user disabled for this catalogue (item b) — keys from
   *  the product set (usp/description/specs/shopperIntent/competitiveness/faq)
   *  or the category set (topParagraph/bottomParagraph). Absent/empty = all
   *  fixed sections generate. Applies to the NEXT run. */
  disabledSections?: string[];

  /** ISO timestamp of the last successful sync (create or refresh-apply). */
  lastSyncAt?: string;
  /** S3 key of the persisted reduced snapshot ({productId → lastmod}) — the diff source. */
  lastSnapshotKey?: string;

  /** ISO timestamp of the last COMPLETED optimize run over this catalogue.
   *  Absent = never optimized: the `product-index.json` matching dictionary
   *  still has raw/sitemap-derived names (no real SKU, no page-extracted
   *  title) instead of the worker's extracted ones, so segment/measurement
   *  matching and content grounding are unreliable. Stamped by the optimize
   *  worker's last-writer (`bumpCounter` in infra/lambda/retailer-optimize)
   *  when a run's counter reaches `total`. Gates catalogue selection in
   *  segment creation (SegmentEditorDialog.tsx) and content creation
   *  (ProductContent.tsx). */
  lastOptimizedAt?: string;

  status: CatalogueStatus;
  createdAt: string;
  updatedAt: string;
}

// ------------------------------------------------------------
// The structured GEO wording rewrite (Phase 2). Stored on the DDB item, NOT in
// vector metadata — it's rendered/edited; the vector only holds the embedding.
// ------------------------------------------------------------

export interface ProductWording {
  /** Unique selling points — why buy THIS, distilled. */
  usp: string;
  /** GEO-optimized descriptive prose. */
  description: string;
  /** Concrete specs + numbers (dimensions, materials, capacity…). */
  specs: string;
  /** Shopper-intent framing — the use-cases / who it's for. */
  shopperIntent: string;
  /** Competitiveness angle — price/quality positioning vs alternatives. */
  competitiveness: string;
  /** FAQ block (question/answer pairs, rendered as structured text). */
  faq: string;
  /** SEO `<title>` (≤~60 chars) — GEO-optimized, editable like the sections above. */
  seoTitle?: string;
  /** SEO meta description (≤~155 chars) — GEO-optimized, editable. */
  seoMetaDescription?: string;
  // --- CATEGORY pages only (product-listing pages) — the product sections above
  //     don't apply; a category is rewritten as SEO + two editorial paragraphs. ---
  /** Intro paragraph shown ABOVE the product grid (what the category covers, how to choose). */
  topParagraph?: string;
  /** Paragraph shown BELOW the product list (buying guidance, FAQ-ish, internal links). */
  bottomParagraph?: string;
  /** Free-form user-defined sections, keyed by the catalogue's `customSections`
   *  key (e.g. { livraison: "…", garantie: "…" }). Absent keys = not generated. */
  custom?: Record<string, string>;
}

/** A user-defined wording section for a catalogue (beyond the 6 fixed ones). */
export interface CatalogueCustomSection {
  /** Stable slug key (used in ProductWording.custom + CSV column). */
  key: string;
  /** Display label + the LLM instruction ("what goes in this section"). */
  label: string;
  /** One-line brief telling the model what to write for this section. */
  instruction?: string;
  /** Generated by default on a run when true (user can still toggle per catalogue). */
  enabledByDefault?: boolean;
}

/**
 * The CURRENT page content the model read, extracted into the same sections as
 * the rewrite — so the UI can show a before/after diff and offer a per-section
 * "keep current vs. use optimized" choice. A section is '' when the page has
 * nothing for it (the rewrite then fills a genuine gap). Captured at analyze
 * time from JSON-LD + visible text; NOT a fresh fetch.
 */
export type OriginalWording = ProductWording;

// ------------------------------------------------------------
// GEO score — DIMENSIONED, rubric-driven for cross-item consistency.
// Each dimension is scored 0-100 by the model against an explicit rubric baked
// into the prompt (mirrors scorer.py's rubric-anchored approach). The OVERALL
// geoScore is computed DETERMINISTICALLY as the weighted sum below — the model
// never emits a free-floating overall number (avoids bracket drift).
// ------------------------------------------------------------

export type GeoScoreDimension =
  | 'specificity' // concrete, unambiguous product detail vs vague marketing
  | 'structuredData' // Schema.org Product JSON-LD present + complete
  | 'shopperIntent' // covers the buying questions / use-cases a shopper asks an AI
  | 'factualDensity' // numbers, specs, materials, measurements — citable facts
  | 'extractability' // scannable, chunk-independent structure (headings/lists/tables)
  | 'authorityEvidence' // real reviews/ratings/certs + comparison vs named alternatives
  | 'qaCoverage'; // FAQ / Q&A block an AI can lift answers from

/** Weights sum to 1.0. Tuned from the GEO research corpus (see docs/features/retailer-catalog.md
 *  §Phase-2 KPI redesign): a page can't ace GEO on prose alone — structured data +
 *  factual density (what AI engines actually CITE) carry the most weight, and two levers
 *  the corpus measured directly now score explicitly: `extractability` (restructuring
 *  ALONE lifted citation rate +17.3% — GEO-SFE) and `authorityEvidence` (authority +
 *  comparative claims are the single highest-leverage signal on LLM rankers). Adding
 *  `authorityEvidence` also closes a prior asymmetry: the rewrite generated a
 *  `competitiveness` section that no dimension scored.
 *
 *  2026-07-21 re-weight: `extractability` 0.10 → 0.14 (taken from `specificity`
 *  0.15 → 0.11). Structure is the largest MEASURED structural lever in the corpus
 *  (+17.3% citation from structure alone — GEO-SFE; +43% extraction accuracy from a
 *  comparison/spec TABLE — see GEO-KNOWLEDGE-BASE §3.14 + the +43% row), yet it was
 *  tied-lowest here. Trimmed `specificity` because `factualDensity` already rewards
 *  the same concrete-fact axis (least-overlap donor). NOTE: this shifts EVERY
 *  product's deterministic `geoScore` — a re-baseline, so existing scores move
 *  slightly until the next optimize run recomputes them; that's expected, not drift. */
export const GEO_SCORE_WEIGHTS: Record<GeoScoreDimension, number> = {
  structuredData: 0.22,
  factualDensity: 0.18,
  extractability: 0.14,
  shopperIntent: 0.15,
  specificity: 0.11,
  authorityEvidence: 0.1,
  qaCoverage: 0.1,
};

/** Human + prompt-facing rubric per dimension. Shipped INTO the model prompt so
 *  scoring is consistent across items and runs; also surfaced in the UI tooltip. */
export const GEO_SCORE_RUBRIC: Record<GeoScoreDimension, { label: string; rubric: string }> = {
  structuredData: {
    label: 'Données structurées',
    rubric:
      '0 = aucun JSON-LD Product. 50 = Schema.org présent mais incomplet (nom seul). 100 = Product complet (nom, marque, description, offres, prix/disponibilité, avis/notes si applicable).',
  },
  factualDensity: {
    label: 'Densité factuelle',
    rubric:
      '0 = aucun chiffre/spec. 50 = quelques mesures. 100 = dimensions, matériaux, capacités, poids — des faits citables par un moteur IA.',
  },
  specificity: {
    label: 'Spécificité',
    rubric:
      "0 = générique/marketing vague ('produit de qualité'). 50 = quelques détails concrets. 100 = chaque affirmation est précise, vérifiable, sans superlatif creux.",
  },
  shopperIntent: {
    label: "Intention d'achat",
    rubric:
      "0 = ne répond à aucune question d'acheteur. 50 = couvre l'usage principal. 100 = couvre pour qui, pour quel usage, dans quelles situations, formulé comme une vraie requête d'acheteur (multi-critères).",
  },
  extractability: {
    label: 'Structure / lisibilité IA',
    rubric:
      "0 = un pavé de texte, aucun titre ni liste. 50 = quelques titres OU listes. 100 = titres hiérarchisés, listes/tableaux (specs, comparatif), paragraphes courts et autoportants qu'un moteur IA peut extraire tels quels.",
  },
  authorityEvidence: {
    label: 'Preuve / autorité',
    rubric:
      "0 = aucune preuve externe. 50 = mentions vagues ('plébiscité'). 100 = preuves RÉELLES et vérifiables (note + nombre d'avis, certifications/labels, tests, garantie) ET un angle comparatif face à des alternatives nommées. NE JAMAIS créditer une preuve inventée.",
  },
  qaCoverage: {
    label: 'Questions / FAQ',
    rubric:
      "0 = aucun bloc Q/R. 50 = 1-2 questions. 100 = FAQ couvrant les objections et questions fréquentes, en phrases qu'un moteur IA peut reprendre.",
  },
};

/** Compute the deterministic overall 0-100 from per-dimension scores. */
export function computeGeoScore(dims: Partial<Record<GeoScoreDimension, number>>): number {
  let total = 0;
  for (const [dim, weight] of Object.entries(GEO_SCORE_WEIGHTS) as [GeoScoreDimension, number][]) {
    total += (dims[dim] ?? 0) * weight;
  }
  return Math.round(total);
}

// ------------------------------------------------------------
// Optimization run — async SQS pipeline (Phase 2). One run fans out one message
// per scoped item; a counter tracks progress. NOT scaled with catalogue size —
// worker maxConcurrency is a fixed low cap so we never overload the retailer site.
// ------------------------------------------------------------

export type OptimizeRunStatus = 'running' | 'completed' | 'failed';

export interface OptimizeRun {
  catalogueId: CatalogueId;
  runId: string;
  status: OptimizeRunStatus;
  total: number;
  completed: number;
  blocked: number; // WAF-blocked pages (surfaced for the whitelist popup)
  failed: number;
  gone?: number; // pages permanently removed from the site (HTTP 404/410) — terminal
  startedAt: string;
  updatedAt: string;
  /** A sample of WAF-blocked URLs so the UI can show the whitelist instructions. */
  blockedSampleUrls?: string[];
  /** Score-band tallies stamped at settle time (history + trend chart). Absent
   *  on runs finalized before this shipped. */
  runStats?: { avgScore: number; critical: number; improve: number; good: number; unscored: number };
  /** Never pruned by the retention cascade (score-history.ts). */
  pinned?: boolean;
}

/** Per-item optimize outcome message shape (worker → DDB item update). */
export type OptimizeItemOutcome = 'fetched' | 'blocked' | 'failed' | 'gone';

// ------------------------------------------------------------
// PRODUCT item — SK = PRODUCT#<categoryPath>#<productId>   (≤50k / catalogue)
// CATEGORY item — SK = CATEGORY#<categoryPath>             (~6k max)
// Common enrichment fields shared by both.
// ------------------------------------------------------------

interface CatalogueItemBase {
  catalogueId: CatalogueId;
  /** Stable extracted id (pr-C1823555 / 80100324 / <uuid>) — the KEY. NOT a url-hash:
   *  protects the refresh-diff from re-slug false churn (docs §5.1). */
  productId: string;
  type: CatalogueItemType;

  urlNormalized: string;
  /** sha1(urlNormalized) — for dedup / fallback keying when productId isn't extractable. */
  urlHash: string;
  name: string;

  categoryId: string;
  /** Breadcrumb path; drives begins_with filters. Full path lives here (DDB), a SHORT
   *  categoryId goes to vector metadata (filterable size is billed per-query). */
  categoryPath: string;

  /** Only set on bilingual catalogues; else implicit from Catalogue.language. */
  language?: string;

  // --- enrichment: absent until the Phase-2 fetch runs ---
  /** 0-100 deterministic overall (computeGeoScore over dimensionScores). Sparse
   *  GSI key (geoScore-index) → "worst pages first". */
  geoScore?: number;
  /** Per-dimension 0-100 scores the model produced against the rubric. */
  dimensionScores?: Partial<Record<GeoScoreDimension, number>>;
  /** One geoScore per manual-run day, retention-pruned by the worker on write
   *  (score-history.ts). `t` = ISO day, `s` = geoScore that day. Feeds the
   *  items-table sparkline. */
  scoreHistory?: Array<{ t: string; s: number }>;
  /** The GEO-optimized rewrite (the proposal). */
  wording?: ProductWording;
  /** The CURRENT page content per section (for before/after diff + keep/optimize). */
  originalWording?: OriginalWording;
  /** One-sentence read-only interpretation of the scores (from the analysis) —
   *  "why this score", e.g. "Bonne densité factuelle mais aucun balisage Schema.org". */
  analysisSummary?: string;
  wordingUpdatedAt?: string;
  /** Hash of the composed embedding text — delta-guard for re-embedding (Phase 3). */
  contentHash?: string;
  fetchStatus?: FetchStatus;
  /** Present once embedded (Phase 3) — the vector key (= productId). */
  vectorKey?: string;
  /** Real SKU extracted from JSON-LD (Product.sku → mpn → gtin*) at analyze
   *  time (item a) — NEVER overwrites `productId` (the refresh-diff identity +
   *  vector key). Used as the "Référence" display + the `ref` match signal
   *  when `productId` looks like a url-hash. */
  extractedSku?: string;
  /** Real product title extracted from JSON-LD Product.name → <title> → og:title
   *  (item a) — NEVER overwrites the user-declared/sitemap `name` silently;
   *  shown alongside it in the UI when they differ materially. */
  extractedTitle?: string;

  lastmod?: string; // from sitemap <lastmod>
  createdAt: string;
  updatedAt: string;
}

export interface CatalogueProduct extends CatalogueItemBase {
  type: 'product';
  /** Product image (free from sitemap image:loc on Auchan/LeroyMerlin). */
  image?: string;
}

export interface CatalogueCategory extends CatalogueItemBase {
  type: 'category';
  productCount: number;
  childCount: number;
  /** Use-case cluster (Feature B measurement grouping). */
  clusterId?: string;
}

// ------------------------------------------------------------
// SYNC item — SK = SYNC#<isoDate>  (ephemeral, TTL 7d) — refresh-diff review.
// No `status` field on products: removed items are HARD-DELETED on apply; the
// S3 snapshot diff is the record of change (docs §5.1 / §7 Phase 1e).
// ------------------------------------------------------------

export type SyncStatus = 'pending_review' | 'applied' | 'rejected';

export interface SyncDiff {
  catalogueId: CatalogueId;
  /** ISO date of the diff. */
  date: string;
  addedIds: string[];
  removedIds: string[];
  /** Same productId, different URL — the product moved. Its DDB row (URL/SK) is
   *  rewritten and its stale vector is dropped (wording must be re-optimized). */
  changedIds?: string[];
  previousCount: number;
  /** Set when the anomaly circuit-breaker tripped (see REMOVAL_ALARM_RATIO). */
  anomalyTripped: boolean;
  status: SyncStatus;
  /** DDB TTL (unix seconds), ~now + 7d. */
  ttl: number;
  createdAt: string;
}

/** Refresh circuit-breaker: halt + require explicit confirm when this share of the
 *  previous catalogue would be REMOVED (guards truncated/WAF/re-structured fetches).
 *  A real site rarely drops >30% overnight. Guard mass-ADD symmetrically. */
export const REMOVAL_ALARM_RATIO = 0.3;
export const ADDITION_ALARM_RATIO = 2.0; // new/prev > 2× ⇒ suspicious

// ------------------------------------------------------------
// product-index.json  (S3 classic, regenerated every sync) — the matching file.
// Loaded once per Feature-B worker into an Aho-Corasick automaton over names +
// a URL→productId regex. NOT the source of truth (DDB is) — a derived projection.
// ------------------------------------------------------------

export interface ProductIndexEntry {
  productId: string;
  name: string;
  url: string;
  /** Alternate names / model numbers to also match in AI-answer text. */
  aliases?: string[];
}

// ============================================================
// GEO Measurement (Phase 4) — Audiences → Segments → Prompts → Run
// ============================================================
// Container model (per the feature request):
//   Audience  — a measurement "view"; contains one or more Segments.
//   Segment   — a customer segment: a SEMANTIC description of the shopper + its
//               config (languages, engines, #prompts, zones, catalogue). Prompts
//               are generated from that, then editable.
// A run sends every segment's prompts to the AI engines (reusing the BP fan-out
// PATTERN, NOT its schema), parses recommendations (product-name match on the
// catalogue product-index + cited-URL domain check + referral-style analysis).

/** Internal AI-engine ids — same run-path names the BP fan-out uses. */
export type MeasurementEngine = 'perplexity' | 'openai' | 'gemini' | 'claude' | 'mistral';

export type AudienceRunStatus = 'draft' | 'running' | 'completed' | 'partial' | 'failed';

/** Funnel-stage distribution for a segment (sums to 100). Shapes the mix of
 *  prompts the generator writes: TOFU = broad discovery / problem-aware,
 *  MOFU = comparison / criteria, BOFU = high buying intent / where-to-buy. */
export interface FunnelMix {
  tofu: number;
  mofu: number;
  bofu: number;
}
export const DEFAULT_FUNNEL_MIX: FunnelMix = { tofu: 25, mofu: 40, bofu: 35 };

/** Curated emojis to personify a segment (visual only) — people, but also
 *  animals, food, objects and symbols. A closed set so the server can validate;
 *  picked in the segment editor, shown on the card. */
export const SEGMENT_AVATARS = [
  // People
  '🧑', '👩', '👨', '🧓', '👵', '👴', '👧', '👦', '🧒', '👶',
  '👩‍🦰', '👨‍🦰', '👩‍🦱', '👨‍🦱', '👩‍🦳', '👨‍🦳', '👱‍♀️', '👱‍♂️',
  '🧕', '👳', '🧔', '👲', '👩‍🎓', '👨‍🎓', '👩‍💼', '👨‍💼',
  '👩‍🔧', '👨‍🔧', '👩‍🍳', '👨‍🍳', '👩‍⚕️', '👨‍⚕️', '🧑‍🌾', '🧑‍🎨',
  '👪', '👫', '🧑‍🤝‍🧑', '🤰', '🧑‍🍼', '🕺', '💃', '🏃', '🚶', '🧘',
  // Smileys & gestures
  '😀', '😍', '🤩', '😎', '🤓', '🥳', '🤑', '🧐', '🤔', '😴',
  '🥰', '😇', '😤', '🥺', '👋', '👍', '👏', '🙌', '💪', '🤝',
  // Animals
  '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯',
  '🦁', '🐮', '🐷', '🐸', '🐵', '🦉', '🦄', '🐝', '🦋', '🐢',
  '🐬', '🐳', '🦅', '🐔', '🦜', '🐺',
  // Food & drink
  '🍏', '🍌', '🍇', '🍓', '🍑', '🍒', '🥑', '🥦', '🥕', '🌽',
  '🍞', '🧀', '🍕', '🍔', '🌮', '🍣', '🍱', '🍜', '🍰', '🍫',
  '☕', '🍵', '🥤', '🍷', '🍺', '🥂',
  // Objects & shopping
  '🛒', '🛍️', '🎁', '💼', '🎒', '👗', '👕', '👟', '👜', '💍',
  '💄', '⌚', '📱', '💻', '🖥️', '📷', '🎧', '🎮', '📚', '✏️',
  '🔧', '🧹', '🧴', '🧸', '🪑', '🛋️', '🏠', '🔑', '💡', '🔍',
  // Travel & places
  '🚗', '🚕', '🚲', '🛴', '✈️', '🚆', '🚚', '⛵', '🏖️', '⛰️',
  '🏢', '🏭', '🏬', '🌍', '🗺️', '📍',
  // Activities & nature
  '⚽', '🏀', '🎾', '🏋️', '🚴', '⛳', '🎣', '🎯', '🎨', '🎬',
  '🎵', '🌱', '🌳', '🌸', '🌵', '☀️', '🌙', '⚡', '🔥', '❄️',
  // Symbols
  '⭐', '🌟', '✨', '❤️', '💙', '💚', '💜', '🧡', '🏆', '🥇',
  '💎', '💰', '📈', '📊', '🎓', '🩺', '⚖️', '🛡️', '🚀', '🔔',
] as const;
export type SegmentAvatar = (typeof SEGMENT_AVATARS)[number];
export const DEFAULT_SEGMENT_AVATAR: SegmentAvatar = '🧑';

/** One configured customer segment inside an Audience. */
export interface AudienceSegment {
  segmentId: string;
  /** Short label, e.g. "Parents pressés" / "Chineurs de bonnes affaires". */
  name: string;
  /** Persona icon emoji (see SEGMENT_AVATARS) — visual only, personifies the
   *  segment in the UI. Optional; defaults to a neutral face when unset. */
  avatar?: string;
  /** SEMANTIC description of the shopper — free text; drives prompt generation.
   *  e.g. "Jeune parent, achats rapides, cherche des basiques fiables et livrables". */
  description: string;
  /** ISO 639-1 languages the prompts are written in. */
  languages: string[];
  /** Engines to query for this segment. */
  engines: MeasurementEngine[];
  /** Geographic zones to localize prompts to (referral-style). Optional. */
  zones?: string[];
  /** Catalogue this segment measures against (its product-index is the match set). */
  catalogueId: CatalogueId;
  /** How many prompts to generate for this segment. */
  promptCount: number;
  /** Funnel-stage distribution (TOFU/MOFU/BOFU) that biases prompt generation.
   *  Defaults to DEFAULT_FUNNEL_MIX when unset. */
  funnelMix?: FunnelMix;
}

/** A generated (then editable) prompt for a segment. */
export interface SegmentPrompt {
  promptId: string;
  segmentId: string;
  text: string;
  textHash: string;
  language: string;
  zone?: string;
  /** false = user archived it; archived prompts are not sent on a run. */
  active: boolean;
  /** The catalogue product closest (semantically) to this prompt — the SKU the
   *  retailer should be winning with. Computed at prompt create/edit time (vector
   *  search) so it's visible/editable before a run; threaded into the run so the
   *  worker doesn't recompute. Absent when the catalogue has no vectors yet. */
  closestSku?: ClosestSku;
}

/** The Audience container (the measurement "view"). */
export interface Audience {
  audienceId: string;
  brandId: string;
  tenantId: string;
  name: string;
  segments: AudienceSegment[];
  /** Last run summary (populated in Phase 4b). */
  lastRunId?: string;
  lastRunStatus?: AudienceRunStatus;
  lastRunAt?: string;
  /** Coverage-tier tallies for the last COMPLETED run (item n's mini-bar) —
   *  stamped at `_finalize_run` alongside `lastRunScore`. Absent on runs
   *  finalized before this shipped or when the tally read failed. */
  lastRunCoverage?: { covered: number; leaked: number; mentioned: number; none: number };
  /** Explicit measurement cadence override; unset → tier default
   *  (resolveMeasureCadence in cadence.ts: daily for non-agency, off for agency). */
  measureCadence?: Cadence;
  /** Stamped the first time the schedule sync runs for this audience, so a
   *  lazy backfill (on read) doesn't re-upsert an already-synced schedule. */
  scheduleSyncedAt?: string;
  createdAt: string;
  updatedAt: string;
}

/** Sane caps to keep a run bounded + the config UI honest. */
export const AUDIENCE_LIMITS = {
  segmentsPerAudience: 10,
  promptsPerSegment: 50,
  /** engines × prompts × segments ceiling per run (fan-out volume guard). */
  maxRunPrompts: 2000,
} as const;

// ------------------------------------------------------------
// Measurement RUN (Phase 4b) — fan-out result rows + rollups.
// One result row per (prompt × engine); a run counter tracks completion
// (BP-fan-out pattern). Parsing = product-index name match + cited-URL domain
// check + BP-referral-style cited/position analysis.
// ------------------------------------------------------------

/** A product the AI answer recommended, matched to the catalogue. */
export interface RecommendedProductHit {
  productId: string;
  name: string;
  /** How it was matched. `name` = Aho-Corasick over the answer text; `url` =
   *  a cited link resolved to a catalogue product; `both` = both signals. */
  matchedBy: 'name' | 'url' | 'both';
  /** 1-indexed position in the answer's recommendation list, when detectable. */
  position?: number;
  /** 1-indexed rank by the ORDER the product's cited buy-link appears among the
   *  answer's citations. More reliable than `position` (which needs a numbered
   *  text list) for URL-matched products. Present only when the match was URL-based
   *  and link order was derivable. */
  linkOrder?: number;
  /** The actual cited buy-link URL that matched this product — drives the
   *  clickable "acheter" link in the UI. Absent for name-only matches. */
  citedUrl?: string;
  /** WHY this product was detected — the worker's own signal list
   *  (`url`/`name`/`ref`), surfaced as a "Signal" column so the UI closes the
   *  loop with the signal-config card instead of re-deriving it from
   *  `matchedBy` alone. */
  detectedBy?: string[];
}

/** Minimal per-row projection for the Synthèse client-side filter dashboard
 *  (item l) — segmentId/engine/language/zone/coverage/score only, so ANY
 *  filter combination recomputes instantly without re-scanning DDB. */
export interface MeasurementFact {
  segmentId: string;
  engine: MeasurementEngine;
  language: string;
  zone?: string;
  coverage: MeasurementResult['coverage'];
  score: number;
}

/** One (prompt × engine) measurement result. */
export interface MeasurementResult {
  audienceId: string;
  runId: string;
  segmentId: string;
  promptId: string;
  engine: MeasurementEngine;
  promptText: string;
  language: string;
  zone?: string;
  /** The retailer's own domain was cited in the answer (brand-covered). */
  retailerCited: boolean;
  /** Products from THIS catalogue the answer recommended (matched). */
  recommendedProducts: RecommendedProductHit[];
  /** The exact surface strings the worker matched (item i) — the SINGLE
   *  SOURCE OF TRUTH for what drove the verdict: the token sequence for
   *  `product`, the SKU string for `sku`, the cited buy-link/retailer-domain
   *  URL for `retailer`, the brand term for `brand`. The response drawer
   *  highlights ONLY these — no client-side re-detection off name/productId —
   *  so it never highlights something that didn't drive the verdict. Absent
   *  on rows written before this shipped (frontend falls back to the old
   *  client-side derivation for those). Capped at 50. */
  matchedTerms?: { term: string; cls: 'product' | 'sku' | 'retailer' | 'brand' }[];
  /** Coverage tier: brand-covered (retailer named/linked) OR assortment-leaked
   *  (a stocked product recommended but NOT via the retailer) OR mentioned
   *  (domain-mode only: retailer named in text, no link, no product match) OR
   *  not-covered. */
  coverage: 'brand_covered' | 'assortment_leaked' | 'mentioned' | 'not_covered';
  /** 0-100, referral-style: driven by presence + position of the retailer/products. */
  score: number;
  /** Best (lowest) position of any matched product, when detectable. */
  bestPosition?: number;
  /** The AI answer text (for the response panel; capped at write time). Absent on
   *  the paginated LIST projection — only the single-result endpoint returns it. */
  content?: string;
  /** Cited source hosts (for domain highlighting). */
  sourceHosts?: string[];
  /** Cited source URLs (rendered as a source list under the answer). */
  sourceUrls?: string[];
  /** The retailer's own domain (highlighted distinctly from other sources). */
  retailerDomain?: string;
  /** Closest own SKU to the prompt (vector search) — the product the retailer
   *  should be winning with. Computed on EVERY result. */
  closestSku?: ClosestSku;
  /** Competitive "why #1 wins" analysis — present when the retailer wasn't #1. */
  competitive?: CompetitiveAnalysis;
  /** Set on a TERMINAL engine error (auth/validation/parse failure) — the call
   *  never produced an answer, so coverage/score are placeholder zeroes and
   *  the drawer shows this message instead of content. */
  error?: string;
  createdAt: string;
}

export interface MeasurementRun {
  audienceId: string;
  runId: string;
  status: AudienceRunStatus;
  total: number; // (active prompts × engines)
  completed: number;
  failed: number;
  startedAt: string;
  updatedAt: string;
  /** 0-100 average of every result row's score (stamped at terminal). Absent
   *  on runs finalized before this shipped — backfilled lazily on first read. */
  overallScore?: number;
  /** Coverage-tier tallies for this run (history + trend chart), stamped at
   *  terminal alongside `overallScore`. */
  coverageCounts?: { covered: number; leaked: number; mentioned: number; none: number };
  resultCount?: number;
  /** Never pruned by the retention cascade (score-history.ts). */
  pinned?: boolean;
}

/** Per-segment rollup for the results UI (computed at read time from result rows). */
export interface SegmentCoverageRollup {
  segmentId: string;
  segmentName: string;
  total: number;
  brandCovered: number;
  assortmentLeaked: number;
  mentioned: number;
  notCovered: number;
  avgScore: number;
  /** Most-recommended products across this segment's prompts. */
  topProducts: Array<{ productId: string; name: string; count: number }>;
}

// ------------------------------------------------------------
// Run SYNTHESIS (Phase 4c) — the "Synthèse" tab. Deterministic aggregation over
// ALL result rows (scales to 100 segments × 100 prompts × engines with ZERO LLM),
// then ONE Nemotron call over a compact digest → prioritized recommendations.
// Computed at run-finalize and CACHED on the MRUN row (reloads are 0-LLM); a
// re-run regenerates. See docs/features/retailer-catalog.md §Phase 4c.
// ------------------------------------------------------------

/** A cluster of prompts where the retailer is NOT recommended, grouped by the
 *  closest own SKU's inferred product family — "write/optimize content for these
 *  N products of type X". The scalability-smart signal: aggregate, don't per-row. */
export interface UncitedCluster {
  /** Human label for the family, e.g. "Chaussures de randonnée". */
  label: string;
  /** How many (prompt × engine) results in this cluster missed the retailer. */
  missCount: number;
  /** Distinct prompts represented (for "N requêtes"). */
  promptCount: number;
  /** The own SKUs closest to these prompts (the pages to strengthen). */
  skus: Array<{ productId: string; name: string; url: string }>;
  /** A few representative prompt texts (for the LLM + UI context). */
  samplePrompts: string[];
}

/** One synthesized recommendation. `target` routes the CTA the same way the
 *  competitive-analysis recs do. */
export interface MeasurementRecommendation {
  /** content = write/produce content (Studio); product = optimize a fiche
   *  (Optimisation produit); positioning = segment/coverage strategy note. */
  target: 'content' | 'product' | 'positioning';
  title: string;
  detail: string;
  impact: 'high' | 'medium' | 'low';
  effort: 'high' | 'medium' | 'low';
  /** Optional own SKUs this rec concerns (for a product-target CTA). */
  productIds?: string[];
}

/** The whole Synthèse payload — deterministic stats + the LLM narrative/recs. */
/** One slice of the deterministic breakdown (item g) — a dimension value
 *  (an engine, a language, a zone, a catalogue, or a segment) with its own
 *  coverage tallies + average score. */
export interface Slice {
  key: string;
  label: string;
  total: number;
  avgScore: number;
  covered: number;
  leaked: number;
  mentioned: number;
  notCovered: number;
}

export interface MeasurementSynthesis {
  runId: string;
  /** Overall coverage tallies across the run. */
  totals: {
    total: number;
    brandCovered: number;
    assortmentLeaked: number;
    mentioned: number;
    notCovered: number;
    avgScore: number;
  };
  /** Deterministic breakdown by engine / language / zone / catalogue / segment
   *  (item g) — pure aggregation, no LLM. Absent on synthesis rows cached
   *  before this shipped (`synthesisVersion < 2`); recomputed on next open. */
  sliceScores?: {
    byEngine: Slice[];
    byLanguage: Slice[];
    byZone: Slice[];
    byCatalogue: Slice[];
    bySegment: Slice[];
  };
  /** Bumped when the cached synthesis shape changes — a stored row with a
   *  lower (or absent) version is stale and gets recomputed on next open,
   *  even without `force`. */
  synthesisVersion?: number;
  /** Coverage sliced by segment / funnel stage / language / zone (weakest first). */
  weakestSegments: Array<{ segmentId: string; segmentName: string; avgScore: number; coveredPct: number }>;
  /** Prompts where you're #2-3 or assortment-leaked — closest to winning. */
  quickWins: Array<{
    promptId: string;
    segmentId: string;
    promptText: string;
    engine: MeasurementEngine;
    coverage: 'assortment_leaked' | 'mentioned' | 'not_covered';
    bestPosition?: number;
    closestSku?: { productId: string; name: string; url: string };
  }>;
  /** Uncited product-family clusters (write/optimize content here). */
  uncitedClusters: UncitedCluster[];
  /** Competitor domains that recurrently outrank you, with how often. */
  topCompetitors: Array<{ domain: string; count: number }>;
  /** LLM-authored consultative synthesis (2-3 paragraphs, brand working language). */
  narrative: string;
  /** LLM-authored, prioritized recommendations. */
  recommendations: MeasurementRecommendation[];
  /** ISO timestamp the synthesis was computed (cache freshness). */
  computedAt: string;
  /** false when the Nemotron call failed → deterministic stats only, empty recs. */
  llmOk: boolean;
}

// ------------------------------------------------------------
// Competitive analysis (Phase 5) — "why #1 wins + what to change".
// Computed per result row (in the run worker) when the retailer is NOT the #1
// recommendation: find the closest own SKU to the prompt (vector search), fetch
// the #1-recommended competitor page, and LLM-diff → concrete recommendations.
// ------------------------------------------------------------

/** The catalogue product closest (semantically) to the prompt — the SKU the
 *  retailer *should* be winning with. */
export interface ClosestSku {
  productId: string;
  name: string;
  url: string;
  /** 0-1 cosine similarity to the prompt. */
  score: number;
  /** Base-table SK (`PRODUCT#…`/`CATEGORY#…`) — lets the worker point-read this
   *  SKU's `wording` for the competitive diff, instead of diffing against a
   *  blank own-product description. Absent on legacy prompts created before
   *  this field existed (self-heals on the next prompt edit/regeneration). */
  sk?: string;
}

/** A recommended fix from the competitive diff. Two kinds route to two places:
 *  - `product_change` → the /product/optimization page for THIS SKU (tagged
 *    "stratégique" so it's filterable), i.e. rewrite the product page.
 *  - `content_idea`   → the content studio (Phase 6) — an article/guide angle,
 *    usually aggregated across prompts on a summary card. */
export interface CompetitiveRecommendation {
  kind: 'product_change' | 'content_idea';
  title: string;
  detail: string;
  /** For product_change: the own SKU to edit (closest SKU). */
  productId?: string;
}

export interface CompetitiveAnalysis {
  /** The #1-recommended item in the answer (competitor or the retailer). */
  winnerName?: string;
  winnerUrl?: string;
  /** True when the retailer's own domain/product was the #1 — nothing to fix. */
  retailerIsWinner: boolean;
  closestSku?: ClosestSku;
  /** One-paragraph "why #1 wins vs your closest SKU" (LLM). Kept for old rows;
   *  new rows populate `whyWinsBullets` instead (item i, structured output). */
  whyWins?: string;
  /** Structured "why #1 wins" — max 3 bullets, ≤140 chars each (item i). */
  whyWinsBullets?: string[];
  /** Structured "what to change" — max 3 bullets, ≤140 chars each (item i). */
  whatToChange?: string[];
  recommendations: CompetitiveRecommendation[];
  /** false when the #1 page couldn't be fetched (WAF) — whyWins is snippet-based. */
  winnerFetched: boolean;
  /** True when the regex pre-filter (item i) found no real cited shop host to
   *  diff against — the LLM call was skipped entirely rather than free-
   *  associating a competitor. */
  noCommercialWinner?: boolean;
  analyzedAt: string;
}

/** Persisted flag on a catalogue product: a strategic change suggested by the
 *  competitive analysis, surfaced + filterable on the optimization page. */
export interface StrategicRecommendation {
  productId: string;
  title: string;
  detail: string;
  /** Where it came from (audience/run) for provenance. */
  sourceAudienceId?: string;
  createdAt: string;
}
